/**
 * Stock: procurement & inventory (UPGRADE-01 R8). Record a purchase lot — debit
 * Inventory, credit a user-selected cash/bank/mobile-money source — and see the
 * per-model inventory position (purchased − financed-out) + purchase history.
 * Read-gated on `ledger.read`; the entry form needs `stock.manage`.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ModelPrice } from "@dfp/api-client";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { Field, FormError, Select, TextInput, apiErrorMessage } from "../components/Form.js";
import { Table, TBody, Td, Th, THead, Tr } from "../components/Table.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { formatMoney } from "../lib/utils.js";

interface AccountOption {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly type: string;
  readonly category: string;
  readonly isActive: boolean;
}

interface InventoryPosition {
  readonly brand: string;
  readonly model: string;
  readonly currency: string;
  readonly purchasedQty: number;
  readonly financedQty: number;
  readonly onHandQty: number;
  readonly avgUnitCostMinor: number;
  readonly onHandValueMinor: number;
}

interface PurchaseView {
  readonly id: string;
  readonly brand: string;
  readonly model: string;
  readonly quantity: number;
  readonly unitCostMinor: number;
  readonly currency: string;
  readonly sourceAccountName: string;
  readonly note: string | null;
  readonly purchaseDate: string;
}

const SOURCE_CATEGORIES = new Set(["cash_on_hand", "bank", "mobile_money"]);

export function StockPage(): JSX.Element {
  const auth = useAuth();
  const perms = auth.user?.permissions ?? [];
  const canRead = perms.includes("ledger.read");
  const canManage = perms.includes("stock.manage");
  const qc = useQueryClient();

  const accountsQ = useQuery({
    queryKey: ["accounting", "accounts"],
    queryFn: () => api.get<readonly AccountOption[]>("/api/accounting/accounts"),
    enabled: canRead,
  });
  const inventoryQ = useQuery({
    queryKey: ["stock", "inventory"],
    queryFn: () => api.get<readonly InventoryPosition[]>("/api/stock/inventory"),
    enabled: canRead,
  });
  const purchasesQ = useQuery({
    queryKey: ["stock", "purchases"],
    queryFn: () => api.get<readonly PurchaseView[]>("/api/stock/purchases"),
    enabled: canRead,
  });

  // Priced models drive the brand/model pickers, so a purchase can only be
  // recorded against a model that's actually in the price book — no free-text
  // typos that would fragment the inventory-position join. Only fetched for the
  // entry form (needs device.read, which stock.manage users have).
  const modelPricesQ = useQuery({
    queryKey: ["model-prices", "for-stock"],
    queryFn: () => api.get<readonly ModelPrice[]>("/api/model-prices"),
    enabled: canManage,
  });

  const sourceAccounts = useMemo(
    () => (accountsQ.data ?? []).filter((a) => a.isActive && SOURCE_CATEGORIES.has(a.category)),
    [accountsQ.data],
  );

  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");

  const pricedModels = useMemo(
    () => (modelPricesQ.data ?? []).filter((m) => m.active),
    [modelPricesQ.data],
  );
  const brandOptions = useMemo(() => {
    const set = new Set<string>();
    for (const m of pricedModels) set.add(m.brand);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [pricedModels]);
  const modelOptions = useMemo(
    () =>
      pricedModels.filter((m) => m.brand === brand).sort((a, b) => a.model.localeCompare(b.model)),
    [pricedModels, brand],
  );
  const [quantity, setQuantity] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [sourceAccountId, setSourceAccountId] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: (body: {
      brand: string;
      model: string;
      quantity: number;
      unitCostMinor: number;
      sourceAccountId: string;
      note?: string;
    }) => api.post("/api/stock/purchases", body),
    onSuccess: () => {
      setBrand("");
      setModel("");
      setQuantity("");
      setUnitCost("");
      setSourceAccountId("");
      setNote("");
      setError(null);
      void qc.invalidateQueries({ queryKey: ["stock", "inventory"] });
      void qc.invalidateQueries({ queryKey: ["stock", "purchases"] });
      void qc.invalidateQueries({ queryKey: ["accounting", "accounts"] });
    },
    onError: (e: unknown) => setError(apiErrorMessage(e)),
  });

  if (!canRead) {
    return (
      <div className="p-6 text-slate-600">
        You do not have access to stock (requires <code>ledger.read</code>).
      </div>
    );
  }

  const inventory = inventoryQ.data ?? [];
  const purchases = purchasesQ.data ?? [];

  function onBrandChange(b: string): void {
    setBrand(b);
    setModel("");
    setUnitCost("");
  }
  function onModelChange(m: string): void {
    setModel(m);
    // Default the unit cost to the model's priced cost basis; operator overrides
    // if the actual purchase price differed.
    const mp = modelOptions.find((x) => x.model === m);
    if (mp) setUnitCost(String(mp.valueAmountMinor));
  }

  function submit(): void {
    setError(null);
    const qty = Number(quantity);
    const cost = Number(unitCost);
    if (!brand.trim() || !model.trim()) return setError("Enter the brand and model.");
    if (!Number.isInteger(qty) || qty <= 0)
      return setError("Quantity must be a positive whole number.");
    if (!Number.isInteger(cost) || cost <= 0)
      return setError("Unit cost must be a positive whole number.");
    if (!sourceAccountId) return setError("Pick the account it's paid from.");
    record.mutate({
      brand: brand.trim(),
      model: model.trim(),
      quantity: qty,
      unitCostMinor: cost,
      sourceAccountId,
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">Stock &amp; inventory</h1>
        <p className="text-sm text-slate-500">
          A purchase posts a balanced journal — debit Inventory, credit the source. On-hand ={" "}
          purchased − financed-out.
        </p>
      </div>

      {canManage ? (
        <Card>
          <CardHeader>
            <CardTitle>Record purchase</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Brand" hint="priced models">
                <Select value={brand} onChange={(e) => onBrandChange(e.target.value)}>
                  <option value="">Select…</option>
                  {brandOptions.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Model">
                <Select
                  value={model}
                  onChange={(e) => onModelChange(e.target.value)}
                  disabled={!brand}
                >
                  <option value="">{brand ? "Select…" : "Pick a brand first"}</option>
                  {modelOptions.map((m) => (
                    <option key={m.id} value={m.model}>
                      {m.model}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Quantity">
                <TextInput
                  inputMode="numeric"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </Field>
              <Field label="Unit cost" hint="minor units">
                <TextInput
                  inputMode="numeric"
                  value={unitCost}
                  onChange={(e) => setUnitCost(e.target.value)}
                />
              </Field>
              <Field label="Paid from" hint="cash / bank / mobile money">
                <Select
                  value={sourceAccountId}
                  onChange={(e) => setSourceAccountId(e.target.value)}
                >
                  <option value="">Select…</option>
                  {sourceAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Note" hint="optional">
                <TextInput value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
            </div>
            <FormError message={error} />
            <Button onClick={submit} disabled={record.isPending}>
              {record.isPending ? "Recording…" : "Record purchase"}
            </Button>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Inventory position</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          <Table>
            <THead>
              <Tr>
                <Th>Model</Th>
                <Th className="text-right">Purchased</Th>
                <Th className="text-right">Financed out</Th>
                <Th className="text-right">On hand</Th>
                <Th className="text-right">Avg cost</Th>
                <Th className="text-right">Value</Th>
              </Tr>
            </THead>
            <TBody>
              {inventory.length === 0 ? (
                <Tr>
                  <Td colSpan={6} className="text-slate-400">
                    No stock purchased yet.
                  </Td>
                </Tr>
              ) : (
                inventory.map((p) => (
                  <Tr key={`${p.brand} ${p.model}`}>
                    <Td>
                      {p.brand} {p.model}
                    </Td>
                    <Td className="text-right tabular-nums">{p.purchasedQty}</Td>
                    <Td className="text-right tabular-nums text-slate-500">{p.financedQty}</Td>
                    <Td className="text-right tabular-nums font-medium">{p.onHandQty}</Td>
                    <Td className="text-right tabular-nums text-slate-500">
                      {formatMoney(p.avgUnitCostMinor, p.currency)}
                    </Td>
                    <Td className="text-right tabular-nums">
                      {formatMoney(p.onHandValueMinor, p.currency)}
                    </Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Purchase history</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          <Table>
            <THead>
              <Tr>
                <Th>Date</Th>
                <Th>Model</Th>
                <Th className="text-right">Qty</Th>
                <Th className="text-right">Unit cost</Th>
                <Th>Paid from</Th>
              </Tr>
            </THead>
            <TBody>
              {purchases.length === 0 ? (
                <Tr>
                  <Td colSpan={5} className="text-slate-400">
                    No purchases recorded yet.
                  </Td>
                </Tr>
              ) : (
                purchases.map((p) => (
                  <Tr key={p.id}>
                    <Td className="text-slate-500">{p.purchaseDate.slice(0, 10)}</Td>
                    <Td>
                      {p.brand} {p.model}
                    </Td>
                    <Td className="text-right tabular-nums">{p.quantity}</Td>
                    <Td className="text-right tabular-nums">
                      {formatMoney(p.unitCostMinor, p.currency)}
                    </Td>
                    <Td className="text-slate-500">{p.sourceAccountName}</Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
        </CardBody>
      </Card>
    </div>
  );
}
